import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { enums } from "../../constants/enum";
import useCustomerService from "../../services/api/customerService";
import useOrderService from "../../services/api/orderService";
import currencyFormatter from "../../util/currencyFormatter";
import dateFormatter from "../../util/dateFormatter";
import { useConfirm } from "../../hooks/confirm";

import {
  ArrowLongRightIcon,
  DeleteIcon,
  EditIcon,
  FlagLineIcon,
  FlagSolidIcon,
} from "../../assets/icons";
import {
  Button,
  Chip,
  IconButton,
  ListBox,
  Loading,
  Modal,
  PageTitle,
  Section,
  SectionSelector,
} from "../../components";
import {
  Checkbox,
  EyeMeasurementInput,
  TextInput,
} from "../../components/forms";
import "./CustomersPage.scss";
import useOrderFormService from "../../services/api/orderFormService";

function OrdersSection({ orders, refetch }) {
  const navigate = useNavigate();
  const { toggleOrderFlag } = useOrderService();

  const mutation = useMutation({
    mutationFn: toggleOrderFlag,
    onSuccess: () => {
      refetch();
    },
    onError: (error) => {
      toast.error(error?.message);
    },
  });

  const handleFlagToggle = useCallback(
    (orderId) => {
      mutation.mutate(orderId);
    },
    [mutation],
  );

  function setChipColor(status) {
    switch (status) {
      case enums.ORDER_STATUSES.CONFIRMED:
        return "warning";
      case enums.ORDER_STATUSES.READY:
        return "info";
      case enums.ORDER_STATUSES.COMPLETED:
        return "success";
      case enums.ORDER_STATUSES.CANCELLED:
        return "danger";
      default:
        return undefined;
    }
  }
  return (
    <Section className="orders-section" title="Orders">
      <div className="table-wrapper">
        <div className="table-title">
          <h3>Orders</h3>
        </div>

        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th>Flag</th>
                <th>Order ID</th>
                <th>Order Date</th>
                <th>Order Amount</th>
                <th>Prescription ID</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {orders?.length > 0 ? (
                orders.map((order) => (
                  <tr key={order.orderId}>
                    <td>
                      <button
                        className="flag-btn"
                        onClick={() => handleFlagToggle(order?.orderId)}
                      >
                        {order?.isFlagged ? (
                          <FlagSolidIcon />
                        ) : (
                          <FlagLineIcon />
                        )}
                      </button>
                    </td>
                    <td>{order.orderId}</td>
                    <td>{dateFormatter(order.orderDate)}</td>
                    <td>{currencyFormatter(order.totalAmount)}</td>
                    <td>{order.orderForm}</td>
                    <td>
                      <Chip
                        text={order?.status}
                        variant={setChipColor(order?.status)}
                      />
                    </td>
                    <td>
                      <Button
                        text="View"
                        prefixIcon={<ArrowLongRightIcon />}
                        onClick={() =>
                          navigate(`/orders/details/${order?.orderId}`)
                        }
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="table-no-data">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}

function PrescriptionFormsSection({
  openModal,
  orderForms,
  orderNumberSequencePrefix = undefined,
}) {
  return (
    <Section className="prescription-forms-section" title="Prescription Forms">
      <div className="table-wrapper">
        <div className="table-title">
          <h3>Prescription Forms</h3>
        </div>

        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th>Prescription ID</th>
                <th>Created Date</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {orderForms.length > 0 ? (
                orderForms.map((form) => (
                  <tr key={form?._id}>
                    <td>{`${
                      orderNumberSequencePrefix ? orderNumberSequencePrefix : ""
                    } ${form?.eyeCheckingNumber}`}</td>
                    <td>{dateFormatter(form?.testDate)}</td>
                    <td>
                      <Button
                        text="View"
                        prefixIcon={<ArrowLongRightIcon />}
                        onClick={() => openModal(form?._id)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="table-no-data">
                    No prescription forms found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}

function CustomerDetailsPage() {
  const confirm = useConfirm();
  const [currentSection, setCurrentSection] = useState(0);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { customerId } = useParams();
  const { getCustomerById, queryKey, deleteCustomer } = useCustomerService();
  const deleteCustomerMutation = useMutation({ mutationFn: deleteCustomer });
  const {
    data: customerData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [queryKey, customerId],
    queryFn: () => getCustomerById(customerId),
    enabled: !!customerId,
    retry: 1,
  });

  if (isLoading) return <Loading variant="fullScreen" />;
  if (error) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
    return navigate(-1, { replace: true });
  }
  const handleCustomerDelete = async () => {
    const confirmDelete = await confirm({
      title: "Delete Customer",
      description: "Are you sure you want to delete this customer?",
    });
    if (!confirmDelete) return;
    deleteCustomerMutation.mutate(customerId, {
      onSuccess: async () => {
        navigate("/customers", { replace: true });
        await queryClient.resetQueries({
          queryKey: [queryKey],
        });
        toast.success("Customer deleted successfully");
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.payload?.message ||
            enums.DEFAULT_ERROR_MESSAGE,
        );
      },
    });
  };
  const openModal = (modalId) => {
    setSelectedPrescriptionId(modalId);
    setShowPrescriptionModal(true);
  };

  return (
    <>
      <div className="page customer-details-page">
        <PageTitle text="Customer Details" prev="/customers">
          <div className="action-list-wide">
            <Link to={`/customers/edit/${customerId}`}>
              <Button text="Edit Details" icon={<EditIcon />} />
            </Link>
            <IconButton
              icon={<DeleteIcon />}
              rounded
              onClick={handleCustomerDelete}
            />
          </div>

          <ListBox
            className="action-list"
            options={[
              {
                text: "Edit details",
                onClick: () => navigate(`/customers/edit/${customerId}`),
              },
              { text: "Delete customer", onClick: handleCustomerDelete },
            ]}
          />
        </PageTitle>
        <Section className="data-sec">
          <div className="left-bx">
            <div className="name">
              <span>Name</span>
              <h2>{customerData?.customer?.nameWithTitle}</h2>
            </div>

            <div className="birthday">
              <span>Birthday</span>
              <p>{dateFormatter(customerData?.customer?.dob)}</p>
            </div>

            <div className="address">
              <span>Address</span>
              <p>
                {customerData?.customer?.addressLine1},{" "}
                {customerData?.customer?.addressLine2},{" "}
                {customerData?.customer?.city}
              </p>
            </div>

            <div className="created">
              <span>Created at</span>
              <p>{dateFormatter(customerData?.customer?.createdAt)}</p>
            </div>

            <div className="type">
              <span>Customer type</span>
              <p>{customerData?.customer?.type}</p>
            </div>
            {customerData?.customer?.type === enums.CUSTOMER_TYPES.EYE_CAMP &&
            customerData?.customer?.campaignId?.name ? (
              <div className="camp">
                <span>Campaign Name</span>
                <p>{customerData?.customer?.campaignId?.name}</p>
              </div>
            ) : null}
          </div>

          <div className="right-bx">
            <div className="id">
              <span>Customer ID</span>
              <p>{customerData?.customer?._id}</p>
            </div>

            <div className="phone">
              <span>Phone number</span>
              <p>{customerData?.customer?.mobileNumber}</p>
            </div>

            <div className="update">
              <span>Last updated at</span>
              <p>{dateFormatter(customerData?.customer?.updatedAt)}</p>
            </div>

            {customerData?.customer?.type === enums.CUSTOMER_TYPES.EYE_CAMP &&
            customerData?.customer?.campaignId?.orderNumberSequencePrefix ? (
              <div className="camp">
                <span>Campaign Id</span>
                <p>
                  {
                    customerData?.customer?.campaignId
                      ?.orderNumberSequencePrefix
                  }
                </p>
              </div>
            ) : null}
            {customerData?.customer?.type === enums.CUSTOMER_TYPES.EYE_CAMP &&
            customerData?.customer?.campaignId?.place ? (
              <div className="camp">
                <span>Campaign Location</span>
                <p>{customerData?.customer?.campaignId?.place}</p>
              </div>
            ) : null}
          </div>
        </Section>

        <SectionSelector
          currentSection={currentSection}
          sectionList={[
            {
              text: "Orders",
              count: customerData?.customer?.orders?.length
                ?.toString()
                .padStart(2, "0"),
            },
            {
              text: "Prescription Forms",
              count: customerData?.customer?.orderForms?.length
                ?.toString()
                .padStart(2, "0"),
            },
          ]}
          onSectionChange={(next) => setCurrentSection(next)}
        />

        {currentSection === 0 ? (
          <OrdersSection
            orders={customerData?.customer?.orders}
            refetch={refetch}
          />
        ) : null}
        {currentSection === 1 ? (
          <PrescriptionFormsSection
            orderForms={customerData?.customer?.orderForms}
            orderNumberSequencePrefix={
              customerData?.customer?.campaignId?.orderNumberSequencePrefix
            }
            openModal={(id) => openModal(id)}
          />
        ) : null}
      </div>
      <PrescriptionModal
        showPrescriptionModal={showPrescriptionModal}
        setShowPrescriptionModal={setShowPrescriptionModal}
        prescriptionId={selectedPrescriptionId}
      />
    </>
  );
}

export default CustomerDetailsPage;

function PrescriptionModal({
  showPrescriptionModal,
  setShowPrescriptionModal,
  prescriptionId,
}) {
  const { getOrderFormById, queryKey } = useOrderFormService();
  const { data, isLoading, error } = useQuery({
    queryKey: [queryKey, prescriptionId],
    queryFn: () => getOrderFormById(prescriptionId),
    enabled: !!prescriptionId,
  });

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }

  const initialValues = {
    eyeCheckingNumber: data?.orderForm?.eyeCheckingNumber,
    CR39: data?.orderForm?.CR39 ? data?.orderForm?.CR39 : "",
    PHOTO_GREY: data?.orderForm?.PHOTO_GREY ? data?.orderForm?.PHOTO_GREY : "",
    MULTI_COATED: data?.orderForm?.MULTI_COATED
      ? data?.orderForm?.MULTI_COATED
      : "",
    PROGRESSIVE: data?.orderForm?.PROGRESSIVE
      ? data?.orderForm?.PROGRESSIVE
      : "",
    EASY_FIT: data?.orderForm?.EASY_FIT ? data?.orderForm?.EASY_FIT : "",
    hb: data?.orderForm?.hb || "",
    distance: {
      right: {
        sph: data?.orderForm?.distance?.right?.sph || null,
        cyl: data?.orderForm?.distance?.right?.cyl || null,
        axis: data?.orderForm?.distance?.right?.axis || 0,
      },
      left: {
        sph: data?.orderForm?.distance?.left?.sph || null,
        cyl: data?.orderForm?.distance?.left?.cyl || null,
        axis: data?.orderForm?.distance?.left?.axis || 0,
      },
    },
    near: {
      right: {
        sph: data?.orderForm?.near?.right?.sph || null,
        cyl: data?.orderForm?.near?.right?.cyl || null,
        axis: data?.orderForm?.near?.right?.axis || 0,
      },
      left: {
        sph: data?.orderForm?.near?.left?.sph || null,
        cyl: data?.orderForm?.near?.left?.cyl || null,
        axis: data?.orderForm?.near?.left?.axis || 0,
      },
    },
    frame: null,
    lense: null,
    discount: 0,
    orderSchedule: "now",
    paymentType: "fullPayment",
    paymentMethod: enums.PAYMENT_METHODS.CASH,
  };
  return (
    <Modal
      className="prescription-modal"
      title="Prescription"
      close={!showPrescriptionModal}
      onClose={() => setShowPrescriptionModal(false)}
    >
      <Formik initialValues={initialValues} enableReinitialize>
        <Form>
          <Section
            title="Prescription Details"
            className="prescription-details"
          >
            <TextInput
              className="prescription-number-input"
              name="eyeCheckingNumber"
              disabled
              label="Prescription Number"
            />

            <div className="meta-box">
              <div className="date">
                <span>Date</span>
                <p>{dateFormatter(data?.orderForm?.testDate)}</p>
              </div>

              {/* <div className="created">
                  <span>Created by</span>
                  <p>
                    <span className="role">Admin</span> Mr. Kalpa Kavindu
                  </p>
                </div> */}
            </div>
          </Section>

          <Section
            title="Eye Measurements"
            className="eye-measurements-section"
          >
            <div className="input-group">
              <TextInput
                label="Half Bifocal (H.B)"
                placeholder="Type here"
                name="hb"
                prefix="+"
                disabled
              />

              <div className="lens-options">
                <span>Lens options</span>

                <div>
                  <div className="checkbox-group">
                    <Checkbox name="CR39" disabled label="CR39" />
                    <Checkbox name="PHOTO_GREY" disabled label="Photo Grey" />
                    <Checkbox
                      name="MULTI_COATED"
                      disabled
                      label="Multi Coated"
                    />
                  </div>

                  <div className="checkbox-group">
                    <Checkbox name="PROGRESSIVE" disabled label="Progressive" />
                    <Checkbox name="EASY_FIT" disabled label="Easy Fit" />
                  </div>
                </div>
              </div>
            </div>

            <div className="table-group">
              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>R</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.right.sph"
                        />
                      </td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.right.cyl"
                        />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="distance.right.axis"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput disabled name="near.right.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput disabled name="near.right.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="near.right.axis"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>L</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.left.sph"
                        />
                      </td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.left.cyl"
                        />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="distance.left.axis"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput disabled name="near.left.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput disabled name="near.left.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="near.left.axis"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
          {data?.orderForm?.lense ? (
            <Section title="Lens Details" className="lens-details-section">
              <div className="right-lens">
                <h3>Right Lens</h3>
                <div className="lens-table">
                  <div className="table-container">
                    <table>
                      <tbody>
                        <tr>
                          <td
                            rowSpan={
                              data?.orderForm?.lense?.right?.selectedLense
                                ?.attributes?.coating ===
                              enums.LENSE_COATINGS.PHOTO_GREY
                                ? 7
                                : 6
                            }
                            className="table-title"
                          >
                            <p>LENS</p>
                          </td>
                          <td className="table-sub-title">Brand</td>
                          <td>
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.brand || " - "}
                          </td>
                        </tr>

                        <tr>
                          <td className="table-sub-title">Type</td>
                          <td>
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.attributes?.lense_type || " - "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Color</td>
                          <td>
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.attributes?.coating || " - "}
                          </td>
                        </tr>

                        {data?.orderForm?.lense?.right?.selectedLense
                          ?.attributes?.coating ===
                        enums.LENSE_COATINGS.PHOTO_GREY ? (
                          <tr>
                            <td className="table-sub-title">Transition</td>
                            <td>
                              {data?.orderForm?.lense?.right?.selectedLense
                                ?.attributes?.transition || " - "}
                            </td>
                          </tr>
                        ) : null}
                        <tr>
                          <td className="table-sub-title">SEG</td>
                          <td>
                            24 x{" "}
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.attributes?.seg
                              ? `${data?.orderForm?.lense?.right?.selectedLense?.attributes?.seg} mm`
                              : "  "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Diameter</td>
                          <td>
                            {data?.orderForm?.lense?.right?.diameter
                              ? `${data?.orderForm?.lense?.right?.diameter} mm`
                              : " - "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Price</td>
                          <td>
                            {currencyFormatter(
                              parseInt(
                                data?.orderForm?.lense?.right?.selectedLense
                                  ?.sellingPrice || 0,
                              ),
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="separator"></div>

              <div className="left-lens">
                <h3>Left Lens</h3>
                <div className="lens-table">
                  <div className="table-container">
                    <table>
                      <tbody>
                        <tr>
                          <td
                            rowSpan={
                              data?.orderForm?.lense?.left?.selectedLense
                                ?.attributes?.coating ===
                              enums.LENSE_COATINGS.PHOTO_GREY
                                ? 7
                                : 6
                            }
                            className="table-title"
                          >
                            <p>LENS</p>
                          </td>
                          <td className="table-sub-title">Brand</td>
                          <td>
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.brand || " - "}
                          </td>
                        </tr>

                        <tr>
                          <td className="table-sub-title">Type</td>
                          <td>
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.attributes?.lense_type || " - "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Color</td>
                          <td>
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.attributes?.coating || " - "}
                          </td>
                        </tr>

                        {data?.orderForm?.lense?.left?.selectedLense?.attributes
                          ?.coating === enums.LENSE_COATINGS.PHOTO_GREY ? (
                          <tr>
                            <td className="table-sub-title">Transition</td>
                            <td>
                              {data?.orderForm?.lense?.left?.selectedLense
                                ?.attributes?.transition || " - "}
                            </td>
                          </tr>
                        ) : null}
                        <tr>
                          <td className="table-sub-title">SEG</td>
                          <td>
                            24 x{" "}
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.attributes?.seg
                              ? `${data?.orderForm?.lense?.left?.selectedLense?.attributes?.seg} mm`
                              : "  "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Diameter</td>
                          <td>
                            {data?.orderForm?.lense?.left?.diameter
                              ? `${data?.orderForm?.lense?.left?.diameter} mm`
                              : " - "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Price</td>
                          <td>
                            {currencyFormatter(
                              parseInt(
                                data?.orderForm?.lense?.left?.selectedLense
                                  ?.sellingPrice || 0,
                              ),
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </Section>
          ) : null}
          {data?.orderForm?.frame ? (
            <Section title="Frame Details" className="frame-details-section">
              <div className="frame-table">
                <div className="table-container">
                  <table>
                    <tr>
                      <td rowSpan={8} className="table-title">
                        <p>FRAME</p>
                      </td>
                      <td className="table-sub-title">Modal</td>
                      <td colSpan={2}>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.model_number
                        }
                      </td>
                    </tr>

                    <tr>
                      <td rowSpan={3} className="table-sub-title">
                        Size
                      </td>
                      <td className="table-sub-sub-title">Lens diagonal</td>
                      <td>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.size?.lens_diagonal
                        }
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-sub-title">Bridge size</td>
                      <td>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.size?.bridge
                        }
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-sub-title">Arm Length</td>
                      <td>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.size?.arm_length
                        }
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-title">Material</td>
                      <td colSpan={2}>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.material
                        }
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Color</td>
                      <td colSpan={2}>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.color
                        }
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-title">P.D</td>
                      <td colSpan={2}>{data?.orderForm?.frame?.pd}</td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Price</td>
                      <td colSpan={2}>
                        {currencyFormatter(
                          parseInt(
                            data?.orderForm?.frame?.selectedFrame
                              ?.sellingPrice || 0,
                          ),
                        )}
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </Section>
          ) : null}
        </Form>
      </Formik>
    </Modal>
  );
}
